import { DomainEntity, Id, Json } from '@homeleite/arch';
import { DateUtils } from '@homeleite/common-utils';
import { ControllerSummary } from '../controllers';
import { ProducerSummary } from '../producers';
import { PropertySummary } from '../properties';
import { Milking } from './milking';
import { Status } from './status.enum';
import { AnimalSummary, MilkedAnimal } from '../animals';
import { AuditData } from '../animals/auditData';

export class MilkControl implements DomainEntity {

    constructor(
        public readonly id = Id.gen(),
        public auditData: AuditData = null,
        public startedAt: Date = null,
        public finishedAt: Date = null,
        public status: string = Status.OPEN,
        public producer: ProducerSummary = null,
        public property: PropertySummary = null,
        public milkings: Milking[] = [],
        public controller: ControllerSummary = null,
        public official = false,
        public eventsReportedAt: Date = null
    ) {}

    get averageLiters(): number {
        return this.totalMilkedVolume / this.totalProducingAnimals;
    }

    get totalProducingAnimals(): number {
        const animals = new Map<string, AnimalSummary>();

        this.milkings.forEach((milking: Milking) => {

            milking.milkedAnimals.forEach((milkedAnimal: MilkedAnimal) => {
                if (milkedAnimal.production > 0) {
                    animals.set(milkedAnimal.animal.id, milkedAnimal.animal);
                }
            });
        });

        const count = Array.from(animals.values()).length;

        return count > 0 ? count : 1;
    }

    get formattedStartDate(): string {
        if (!this.startedAt) return '';
        return new Date(this.startedAt).toLocaleDateString('pt-BR');
    }

    get formattedStartTime(): string {
        if (!this.startedAt) return '';
        return new Date(this.startedAt).toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    get isOpenStatus(): boolean {
        return this.status === Status.OPEN;
    }

    get controlStatus(): string {
        return  this.isOpenStatus ? 'ABERTO' : 'FINALIZADO';
    }

    get totalMilkedVolume() {
        return this.milkings.map((it) => it.totalMilkedVolume).reduce((sum, current) => sum + current, 0);
    }

    get isFinished(): boolean {
        return this.finishedAt != null;
    }

    get hasMilkings(): boolean {
        return this.milkings.length > 0;
    }

    get milkedAnimalsId(): Set<string> {
        const milkedAnimalsId = new Set<string>();

        this.milkings.forEach(milking => {
            milking.milkedAnimals.forEach(milkedAnimal => {
                milkedAnimalsId.add(milkedAnimal.animal.id);
            });
        });

        return milkedAnimalsId;
    }

    calculateTotalNewCows(penultimateControl: MilkControl): number {
        if (!penultimateControl) {
            return this.milkedAnimalsId.size;
        }

        const lastFinishedMilkedAnimalsId = this.milkedAnimalsId;
        const penultimateFinishedMilkedAnimalsId = penultimateControl.milkedAnimalsId;

        let newCowsCount = 0;

        for (const milkedAnimalId of lastFinishedMilkedAnimalsId) {
            if (!penultimateFinishedMilkedAnimalsId.has(milkedAnimalId)) {
                newCowsCount++;
            }
        }
        return newCowsCount;
    }


    toJson(): any {
        return Json.to.simple(this, {
            startedAt: this.startedAt?.toISOString() ?? null,
            finishedAt: this.finishedAt?.toISOString() ?? null,
            producer: this.producer?.toJson() ?? null,
            property: this.property?.toJson() ?? null,
            milkings: this.milkings.map((milking) => milking.toJson()) ?? null,
            controller: this.controller?.toJson() ?? null,
            auditData: this.auditData?.toJson() ?? null,
            eventsReportedAt: this.eventsReportedAt?.toISOString() ?? null
        });
    }

    static fromJson(json?: any): MilkControl {
        return json
            ? Json.from.simple(json, MilkControl, {
                startedAt: json.startedAt
                    ? (typeof json.startedAt === 'string'
                        ? DateUtils.parseLocalDateTimeWithSeconds(json.startedAt)
                        : json.startedAt
                    )
                    : null,
                finishedAt: json.finishedAt
                    ? (typeof json.finishedAt === 'string'
                        ? DateUtils.parseLocalDateTimeWithSeconds(json.finishedAt)
                        : json.finishedAt
                    )
                    : null,
                producer: json.producer
                    ? ProducerSummary.fromJson(json.producer)
                    : json.producerId
                      ? ProducerSummary.fromJson({ id: json.producerId })
                      : null,
                property: json.property ? PropertySummary.fromJson(json.property) : null,
                milkings: json.milkings ? Json.from.array(json.milkings, Milking.fromJson) : null,
                controller: json.controller ? ControllerSummary.fromJson(json.controller) : null,
                auditData: json.auditData ? AuditData.fromJson(json.auditData) : null,
                eventsReportedAt: json.eventsReportedAt ? new Date(json.eventsReportedAt) : null
            })
            : null;
    }

}

export class MilkingControlCurrentAndPrevious {
    constructor(
        public current: MilkControl = null,
        public previous: MilkControl = null
    ) {}

    static fromJson(json?: any): MilkingControlCurrentAndPrevious {
        return json
            ? Json.from.simple(json, MilkingControlCurrentAndPrevious, {
                current: MilkControl.fromJson(json.current),
                previous: MilkControl.fromJson(json.previous)
            })
            : null;
    }
}
